import { db } from "@/lib/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  serverTimestamp,
} from "firebase/firestore";

export type ResourceType = "current_affairs" | "study_materials" | "blog_posts" | "test_series" | "pyq";

export type Tag = {
  id?: string;
  name: string;
  slug: string;
  createdAt?: any;
};

export interface ResourceItem {
  id?: string;
  title: string;
  slug?: string;
  description: string;
  content?: string;
  link?: string;
  imageUrls?: string[];
  documentUrls?: string[];
  tagIds?: string[];
  category?: string; // legacy / used for PYQs label chips
  createdAt?: any;
  updatedAt?: any;
}

const COLLECTION_MAP: Record<ResourceType | "courses", string> = {
  current_affairs: "articles",
  study_materials: "study_materials",
  blog_posts: "blog_posts",
  test_series: "test_series",
  pyq: "pyq",
  courses: "courses",
};

function formatDDMMYYYY(d: Date) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

export function slugify(input: string) {
  return String(input || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function ensureUniqueSlugInCollection(
  colName: string,
  requestedBase: string,
  excludeId?: string
) {
  const base = slugify(requestedBase) || "item";

  // First check: the base slug itself
  let candidate = base;
  for (let attempt = 0; attempt < 10; attempt++) {
    if (attempt > 0) {
      candidate = `${base}-${attempt}`;
    }
    
    const q = query(collection(db, colName), where("slug", "==", candidate), limit(1));
    const snap = await getDocs(q);
    const hit = snap.docs[0];
    
    if (!hit) return candidate;
    if (excludeId && hit.id === excludeId) return candidate;
  }

  // Fallback: If 10 attempts fail (unlikely), add a small random token
  return `${base}-${Math.random().toString(36).slice(2, 6)}`;
}

export async function upsertResource(type: ResourceType, data: Partial<ResourceItem>) {
  const colName = COLLECTION_MAP[type];
  if (!colName) throw new Error("Invalid resource type");

  const { id, ...rest } = data;
  const shouldHaveSlug = true; // All resources should have professional slugs
  
  let uniqueSlug = rest.slug;

  if (shouldHaveSlug) {
    // If it's a new item or if the slug is being manually changed/initialized
    // We only force a slug update if it's currently missing or if we're explicitly told to
    const baseForSlug = rest.slug || rest.title || "item";
    uniqueSlug = await ensureUniqueSlugInCollection(colName, baseForSlug, id);
  }

  if (id) {
    const docRef = doc(db, colName, id);
    // On update, we only update the slug if it was provided in the data
    // This prevents accidental slug changes when editing other fields
    const updateData: any = {
      ...rest,
      updatedAt: serverTimestamp(),
    };
    if (shouldHaveSlug && rest.slug) {
       updateData.slug = uniqueSlug;
    }

    await updateDoc(docRef, updateData);
    return { success: true, id };
  }

  const docRef = await addDoc(collection(db, colName), {
    ...rest,
    ...(shouldHaveSlug ? { slug: uniqueSlug } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { success: true, id: docRef.id };
}

export async function deleteResource(type: ResourceType, id: string) {
  const colName = COLLECTION_MAP[type];
  await deleteDoc(doc(db, colName, id));
  return { success: true };
}

export async function getResources(type: ResourceType, onlyVisible = false) {
  const colName = COLLECTION_MAP[type];
  // Remove orderBy to avoid mandatory Firestore index creation in production
  const q = query(collection(db, colName));
  const querySnapshot = await getDocs(q);
  const items = querySnapshot.docs.map((d) => ({
    id: d.id,
    ...(d.data() as any),
  })) as (ResourceItem & { isVisible?: boolean })[];

  // Sort in-memory instead
  const sorted = items.sort((a, b) => {
    const timeA = a.createdAt?.seconds || 0;
    const timeB = b.createdAt?.seconds || 0;
    return timeB - timeA;
  });

  if (onlyVisible) {
    return sorted.filter(item => item.isVisible !== false);
  }
  return sorted;
}

export async function getResourceBySlug(type: ResourceType | "courses", slugOrId: string) {
  const colName = COLLECTION_MAP[type];
  
  // 1. Try Slug Lookup
  const q = query(collection(db, colName), where("slug", "==", slugOrId), limit(1));
  const snap = await getDocs(q);
  if (!snap.empty) {
    const hit = snap.docs[0];
    return { id: hit.id, ...(hit.data() as any) } as ResourceItem;
  }

  // 2. Fallback: Try ID Lookup (for legacy links)
  try {
    const docSnap = await getDoc(doc(db, colName, slugOrId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...(docSnap.data() as any) } as ResourceItem;
    }
  } catch (e) {
    // Silent fail for ID lookup if ID is malformed
  }

  return null;
}

export async function getAllHomeResources() {
  const [currentAffairs, testSeries, pyqs] = await Promise.all([
    getResources("current_affairs", true),
    getResources("test_series", true),
    getResources("pyq", true),
  ]);

  return {
    articles: currentAffairs.slice(0, 4), // keep key name for existing home component
    testSeries: testSeries.slice(0, 4),
    pyqs: pyqs.slice(0, 4),
  };
}

export async function getTags() {
  const q = query(collection(db, "tags"), orderBy("name", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Tag[];
}

export async function upsertTag(data: Partial<Tag>) {
  const name = String(data.name || "").trim();
  if (!name) throw new Error("Tag name required");
  const slug = slugify(String(data.slug || name));
  const { id, ...rest } = data;
  if (id) {
    await updateDoc(doc(db, "tags", id), { ...rest, name, slug, updatedAt: serverTimestamp() } as any);
    return { success: true, id };
  }
  const ref = await addDoc(collection(db, "tags"), { name, slug, createdAt: serverTimestamp() });
  return { success: true, id: ref.id };
}

